import { useEffect, useState } from "react";
import { alfredService, KycFallback } from "src/api";

/**
 * Resolves the `KYC_UPDATE_REQUIRED` fallback schema for the deposit /
 * withdrawal pages.
 *
 * NOTE / deviation from the original plan: the intended home for this was
 * `DepositWithdrawContext` (parse + expose `alfredKycFallback` next to
 * `alfredIneligibilityCode` off the single bootstrap fetch). That file is
 * outside this worktree's writable roots in the current environment, so the
 * parsing lives here instead. To avoid a redundant call on every mount, the
 * hook only re-queries eligibility when the context-provided code already
 * indicates the fallback state — every other gate state short-circuits to
 * `null` and the existing `AlfredIneligibleBlocker` path is untouched.
 *
 * @param ineligibilityCode the code from `useDepositWithdrawContext()`
 *   (`null` when eligible).
 */
export const useAlfredKycFallbackGate = (
  ineligibilityCode: string | null,
): { alfredKycFallback: KycFallback | null; loading: boolean } => {
  const [alfredKycFallback, setAlfredKycFallback] = useState<KycFallback | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!ineligibilityCode || !ineligibilityCode.startsWith("KYC_UPDATE_REQUIRED")) {
      setAlfredKycFallback(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    alfredService
      .getEligibility()
      .then((eligibility) => {
        if (cancelled) return;
        setAlfredKycFallback(
          !eligibility.eligible && eligibility.fallback ? eligibility.fallback : null,
        );
      })
      .catch((err) => {
        console.error("Failed to resolve Alfred KYC fallback:", err);
        if (!cancelled) setAlfredKycFallback(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ineligibilityCode]);

  return { alfredKycFallback, loading };
};
